import { exportToExcel as exportJsonToExcel } from 'react-json-to-excel'
import { Document, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer'
import { getFormattedDate } from './dateFormat'
import config from '../config.json'
import React from 'react'

const parseNum = (val) =>
    val ? parseFloat((val + '').replace(/\./g, '').replace(',', '.')) : 0

const sumCol = (data, col) =>
    data.reduce((acc, x) => acc + parseNum(x[col]), 0)
        .toLocaleString('es-BO', { minimumFractionDigits: 2 })

const formatNum = (val) =>
    val != null
        ? Number(val).toLocaleString('es-BO', { minimumFractionDigits: 2 })
        : '0,00'

const getDocName = () => getFormattedDate() + '.SoyaSari'

const S = StyleSheet.create({
    page: { flexDirection: 'column', padding: 8 },
    // Encabezado
    headerBar: {
        backgroundColor: '#4b5563', padding: 5, marginBottom: 6,
        flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    },
    headerTitle: { fontSize: 11, fontWeight: 'bold', color: 'white' },
    headerSub:   { fontSize: 8, color: '#d1d5db' },
    // Filas de tabla
    rowHeader:  { flexDirection: 'row', backgroundColor: '#4b5563' },
    row:        { flexDirection: 'row', backgroundColor: '#ffffff' },
    rowAlt:     { flexDirection: 'row', backgroundColor: '#f9fafb' },
    rowTotal:   { flexDirection: 'row', backgroundColor: '#4b5563' },
    rowSub:     { flexDirection: 'row', backgroundColor: '#e5e7eb' },
    rowSubAlt:  { flexDirection: 'row', backgroundColor: '#f3f4f6' },
    rowEgreso:  { flexDirection: 'row', backgroundColor: '#fef2f2' },
    rowEgresoAlt: { flexDirection: 'row', backgroundColor: '#fff5f5' },
    // Celdas
    cellH: { fontSize: 7, borderWidth: 0.5, borderColor: '#4b5563', padding: 2, color: 'white', fontWeight: 'bold' },
    cell:  { fontSize: 7, borderWidth: 0.5, borderColor: '#d1d5db', padding: 2, color: '#1f2937' },
    cellT: { fontSize: 7, borderWidth: 0.5, borderColor: '#4b5563', padding: 2, color: 'white', fontWeight: 'bold' },
    cellS: { fontSize: 6.5, borderWidth: 0.5, borderColor: '#d1d5db', padding: 2, color: '#374151' },
    cellSH: { fontSize: 6.5, borderWidth: 0.5, borderColor: '#9ca3af', padding: 2, color: '#6b7280', fontStyle: 'italic', backgroundColor: '#e5e7eb' },
    right:  { textAlign: 'right' },
    center: { textAlign: 'center' },
    indent: { paddingLeft: 8 },
    // Separador entre bloques
    divider: { height: 6 },
})

// CUADERNOS

// Columnas principales del cuaderno
const C_COLS = [
    { key: 'codigo',                        label: 'Código',      flex: 0.9 },
    { key: 'nombre_completo',               label: 'Responsable', flex: 2 },
    { key: 'efectivo_total',                label: 'Efectivo',    flex: 1.1, right: true },
    { key: 'transferencia_total',           label: 'Transfer.',   flex: 1.1, right: true },
    { key: 'garantias_total_efectivo',      label: 'Gtía. Ef.',   flex: 1,   right: true },
    { key: 'garantias_total_transferencia', label: 'Gtía. Tr.',   flex: 1,   right: true },
    { key: 'efectivo_total_neto',           label: 'Neto',        flex: 1.1, right: true },
    { key: 'revisado',                      label: 'Estado',      flex: 0.9, center: true },
    { key: 'createdAt',                     label: 'Fecha',       flex: 1.3 },
]
const C_SUM_KEYS = [
    'efectivo_total', 'transferencia_total',
    'garantias_total_efectivo', 'garantias_total_transferencia',
    'efectivo_total_neto',
]
// Sub-columnas ventas por producto
const V_COLS = [
    { label: 'Producto',      flex: 2 },
    { label: 'V. Efectivo',   flex: 1.1, right: true },
    { label: 'V. Transfer.',  flex: 1.1, right: true },
    { label: 'G. Efectivo',   flex: 1,   right: true },
    { label: 'G. Transfer.',  flex: 1,   right: true },
]
const V_KEYS = ['producto', 'ventas_efectivo', 'ventas_transferencia', 'garantia_efectivo', 'garantia_transferencia']

const CuadernoDocument = ({ data, sheetName }) => {
    const fecha = new Date().toLocaleDateString('es-BO')
    const totales = {}
    C_SUM_KEYS.forEach(k => { totales[k] = sumCol(data, k) })

    return (
        <Document>
            <Page style={S.page} orientation='landscape'>
                {/* Encabezado */}
                <View style={S.headerBar}>
                    <Text style={S.headerTitle}>{sheetName} — Cuadernos</Text>
                    <Text style={S.headerSub}>Generado: {fecha}</Text>
                </View>

                {/* Cabecera de la tabla */}
                <View style={S.rowHeader}>
                    {C_COLS.map(col => (
                        <Text key={col.key} style={[S.cellH, { flex: col.flex }]}>{col.label}</Text>
                    ))}
                </View>

                {/* Fila principal + desglose de ventas por productos */}
                {data.map((item, idx) => {
                    const productos = item.ventas_por_producto?.filter(vp => vp.producto_id !== null) ?? []
                    const totalVp   = item.ventas_por_producto?.find(vp => vp.producto_id === null)
                    const hasVentas = productos.length > 0

                    return (
                        <View key={item.id} wrap={false}>
                            {/* Fila principal del cuaderno */}
                            <View style={idx % 2 === 0 ? S.row : S.rowAlt}>
                                {C_COLS.map(col => {
                                    let val = item[col.key]
                                    if (col.key === 'revisado') val = val ? 'Revisado' : 'Pendiente'
                                    return (
                                        <Text
                                            key={col.key}
                                            style={[S.cell, { flex: col.flex }, col.right && S.right, col.center && S.center]}
                                        >
                                            {val ?? ''}
                                        </Text>
                                    )
                                })}
                            </View>

                            {/* Ventas por producto — justo debajo de su cuaderno */}
                            {hasVentas && (
                                <>
                                    {/* Sub-cabecera */}
                                    <View style={S.rowSub}>
                                        {/* Sangría para indicar que es hijo */}
                                        <Text style={[S.cellSH, { flex: 0.9 }]}>{''}</Text>
                                        {V_COLS.map(col => (
                                            <Text key={col.label} style={[S.cellSH, { flex: col.flex }, col.right && S.right]}>
                                                {col.label}
                                            </Text>
                                        ))}
                                    </View>
                                    {/* Filas de productos */}
                                    {productos.map((vp, vi) => (
                                        <View key={vi} style={vi % 2 === 0 ? S.rowSub : S.rowSubAlt}>
                                            <Text style={[S.cellS, { flex: 0.9 }]}>{''}</Text>
                                            {V_KEYS.map(k => (
                                                <Text
                                                    key={k}
                                                    style={[S.cellS, { flex: V_COLS[V_KEYS.indexOf(k)].flex }, V_COLS[V_KEYS.indexOf(k)].right && S.right]}
                                                >
                                                    {vp[k] ?? ''}
                                                </Text>
                                            ))}
                                        </View>
                                    ))}
                                    {/* Total de ventas */}
                                    {totalVp && (
                                        <View style={S.rowTotal}>
                                            <Text style={[S.cellT, { flex: 0.9 }]}>{''}</Text>
                                            {V_KEYS.map((k, ki) => (
                                                <Text
                                                    key={k}
                                                    style={[S.cellT, { flex: V_COLS[ki].flex }, V_COLS[ki].right && S.right]}
                                                >
                                                    {k === 'producto' ? 'TOTAL' : (totalVp[k] ?? '')}
                                                </Text>
                                            ))}
                                        </View>
                                    )}
                                </>
                            )}
                        </View>
                    )
                })}

                {/* Fila de totales generales */}
                <View style={S.rowTotal}>
                    {C_COLS.map(col => (
                        <Text
                            key={col.key}
                            style={[S.cellT, { flex: col.flex }, col.right && S.right]}
                        >
                            {C_SUM_KEYS.includes(col.key)
                                ? totales[col.key]
                                : col.key === 'codigo' ? 'TOTAL' : ''}
                        </Text>
                    ))}
                </View>
            </Page>
        </Document>
    )
}

export const exportCuadernoToPdf = (data) => {
    const docName   = getDocName()
    const sheetName = config.simpleName ?? ''
    pdf(<CuadernoDocument data={data} sheetName={sheetName} />)
        .toBlob()
        .then(blob => {
            const link = document.createElement('a')
            link.href  = URL.createObjectURL(blob)
            link.download = docName + '_cuadernos.pdf'
            link.click()
        })
}

export const exportCuadernoToExcel = (data) => {
    const docName = getDocName()
    const rows = []

    data.forEach(item => {
        // Fila principal
        rows.push({
            'Código':          item.codigo,
            'Responsable':     item.nombre_completo,
            'Rol':             item.roles,
            'Efectivo Bs.':    item.efectivo_total,
            'Transfer. Bs.':   item.transferencia_total,
            'Gtía. Ef. Bs.':   item.garantias_total_efectivo,
            'Gtía. Tr. Bs.':   item.garantias_total_transferencia,
            'Neto Bs.':        item.efectivo_total_neto,
            'Estado':          item.revisado ? 'Revisado' : 'Pendiente',
            'Fecha':           item.createdAt,
            'Producto':        '',
            'V. Efectivo':     '',
            'V. Transfer.':    '',
            'G. Efectivo':     '',
            'G. Transfer.':    '',
        })
        // Filas de ventas por producto debajo
        const productos = item.ventas_por_producto?.filter(vp => vp.producto_id !== null) ?? []
        productos.forEach(vp => {
            rows.push({
                'Código':       '',
                'Responsable':  '',
                'Rol':          '',
                'Efectivo Bs.': '',
                'Transfer. Bs.':'',
                'Gtía. Ef. Bs.':'',
                'Gtía. Tr. Bs.':'',
                'Neto Bs.':     '',
                'Estado':       '',
                'Fecha':        '',
                'Producto':     vp.producto,
                'V. Efectivo':  vp.ventas_efectivo,
                'V. Transfer.': vp.ventas_transferencia,
                'G. Efectivo':  vp.garantia_efectivo,
                'G. Transfer.': vp.garantia_transferencia,
            })
        })
        // Total de ventas
        const totalVp = item.ventas_por_producto?.find(vp => vp.producto_id === null)
        if (totalVp) {
            rows.push({
                'Código':       '',
                'Responsable':  '',
                'Rol':          '',
                'Efectivo Bs.': '',
                'Transfer. Bs.':'',
                'Gtía. Ef. Bs.':'',
                'Gtía. Tr. Bs.':'',
                'Neto Bs.':     '',
                'Estado':       '',
                'Fecha':        '',
                'Producto':     'TOTAL',
                'V. Efectivo':  totalVp.ventas_efectivo,
                'V. Transfer.': totalVp.ventas_transferencia,
                'G. Efectivo':  totalVp.garantia_efectivo,
                'G. Transfer.': totalVp.garantia_transferencia,
            })
        }
    })

    // Fila de totales generales
    rows.push({
        'Código':          'TOTAL',
        'Responsable':     '',
        'Rol':             '',
        'Efectivo Bs.':    sumCol(data, 'efectivo_total'),
        'Transfer. Bs.':   sumCol(data, 'transferencia_total'),
        'Gtía. Ef. Bs.':   sumCol(data, 'garantias_total_efectivo'),
        'Gtía. Tr. Bs.':   sumCol(data, 'garantias_total_transferencia'),
        'Neto Bs.':        sumCol(data, 'efectivo_total_neto'),
        'Estado': '', 'Fecha': '', 'Producto': '',
        'V. Efectivo': '', 'V. Transfer.': '', 'G. Efectivo': '', 'G. Transfer.': '',
    })

    exportJsonToExcel([{ sheetName: 'Cuadernos', details: rows }], docName, true)
}

// ESTADO DE CUENTAS

const buildEstadoCols = (aditionalCols) => [
    { label: 'Cuenta', flex: 2.5 },
    ...aditionalCols.map(col => ({ label: col, flex: 1.2, right: true })),
]

// Sub-columnas desglose
const ING_ROWS = [
    { key: 'ventas_carros',  label: 'Ventas carros'  },
    { key: 'ventas_tiendas', label: 'Ventas tiendas' },
    { key: 'otros_ingresos', label: 'Otros ingresos' },
    { key: 'total_ingresos', label: 'Total ingresos', bold: true },
]

const EstadoDocument = ({ data, aditionalCols, sheetName }) => {
    const fecha    = new Date().toLocaleDateString('es-BO')
    const mainCols = buildEstadoCols(aditionalCols)
    // Totales por índice de cantidades[]
    const totales  = (() => {
        const len = data[0]?.cantidades?.length ?? 0
        return Array.from({ length: len }, (_, i) =>
            data.reduce((acc, row) => acc + (row.cantidades?.[i] ?? 0), 0)
        )
    })()

    return (
        <Document>
            <Page style={S.page} orientation='landscape'>
                {/* Encabezado */}
                <View style={S.headerBar}>
                    <Text style={S.headerTitle}>{sheetName} — Estado de Cuentas</Text>
                    <Text style={S.headerSub}>Generado: {fecha}</Text>
                </View>

                {/* Cabecera de tabla + columna "Desglose" */}
                <View style={S.rowHeader}>
                    {mainCols.map(col => (
                        <Text key={col.label} style={[S.cellH, { flex: col.flex }, col.right && S.right]}>
                            {col.label}
                        </Text>
                    ))}
                </View>

                {/* Fila principal + desglose juntos por cuenta */}
                {data.map((item, idx) => {
                    const ing = item.desglose?.ingresos
                    const egr = item.desglose?.egresos
                    const neto = item.desglose?.neto

                    return (
                        <View key={item.id} wrap={false}>
                            {/* Fila principal */}
                            <View style={idx % 2 === 0 ? S.row : S.rowAlt}>
                                <Text style={[S.cell, { flex: 2.5 }]}>{item.nombre}</Text>
                                {(item.cantidades ?? []).map((val, ci) => (
                                    <Text key={ci} style={[S.cell, { flex: 1.2 }, S.right]}>
                                        {formatNum(val)}
                                    </Text>
                                ))}
                            </View>

                            {/* Sub-sección ingresos */}
                            {ing && (
                                <>
                                    <View style={S.rowSub}>
                                        <Text style={[S.cellSH, { flex: 2.5 }]}>  ▸ Ingresos</Text>
                                        {aditionalCols.map((_, ci) => (
                                            <Text key={ci} style={[S.cellSH, { flex: 1.2 }]}>{''}</Text>
                                        ))}
                                    </View>
                                    {ING_ROWS.map((r, ri) => (
                                        <View key={r.key} style={ri % 2 === 0 ? S.rowSub : S.rowSubAlt}>
                                            <Text style={[S.cellS, { flex: 2.5 }, S.indent]}>{r.label}</Text>
                                            <Text style={[S.cellS, { flex: 1.2 }, S.right]}>{formatNum(ing[r.key])}</Text>
                                            {aditionalCols.slice(1).map((_, ci) => (
                                                <Text key={ci} style={[S.cellS, { flex: 1.2 }]}>{''}</Text>
                                            ))}
                                        </View>
                                    ))}
                                </>
                            )}

                            {/* Sub-sección egresos */}
                            {egr && (
                                <>
                                    <View style={S.rowSub}>
                                        <Text style={[S.cellSH, { flex: 2.5 }]}>  ▸ Egresos</Text>
                                        {aditionalCols.map((_, ci) => (
                                            <Text key={ci} style={[S.cellSH, { flex: 1.2 }]}>{''}</Text>
                                        ))}
                                    </View>
                                    {egr.conceptos?.length > 0
                                        ? egr.conceptos.map((c, ci) => (
                                            <View key={ci} style={ci % 2 === 0 ? S.rowEgreso : S.rowEgresoAlt}>
                                                <Text style={[S.cellS, { flex: 2.5 }, S.indent]}>{c.tipo}</Text>
                                                <Text style={[S.cellS, { flex: 1.2 }, S.right, { color: '#dc2626' }]}>
                                                    {formatNum(c.totalAbs)}
                                                </Text>
                                                {aditionalCols.slice(1).map((_, i) => (
                                                    <Text key={i} style={[S.cellS, { flex: 1.2 }]}>{''}</Text>
                                                ))}
                                            </View>
                                        ))
                                        : (
                                            <View style={S.rowSub}>
                                                <Text style={[S.cellS, { flex: 2.5 + aditionalCols.length * 1.2 }, S.center, { color: '#9ca3af', fontStyle: 'italic' }]}>
                                                    Sin egresos
                                                </Text>
                                            </View>
                                        )
                                    }
                                    {/* Total egresos */}
                                    <View style={S.rowTotal}>
                                        <Text style={[S.cellT, { flex: 2.5 }]}>  Total egresos</Text>
                                        <Text style={[S.cellT, { flex: 1.2 }, S.right]}>
                                            {formatNum(egr.total_egresos_abs)}
                                        </Text>
                                        {aditionalCols.slice(1).map((_, ci) => (
                                            <Text key={ci} style={[S.cellT, { flex: 1.2 }]}>{''}</Text>
                                        ))}
                                    </View>
                                </>
                            )}

                            {/* Neto de la cuenta */}
                            {neto != null && (
                                <View style={S.rowTotal}>
                                    <Text style={[S.cellT, { flex: 2.5 }]}>  Neto</Text>
                                    <Text style={[S.cellT, { flex: 1.2 }, S.right]}>
                                        {formatNum(neto)}
                                    </Text>
                                    {aditionalCols.slice(1).map((_, ci) => (
                                        <Text key={ci} style={[S.cellT, { flex: 1.2 }]}>{''}</Text>
                                    ))}
                                </View>
                            )}

                            {/* Separador visual */}
                            <View style={S.divider} />
                        </View>
                    )
                })}

                {/* Fila totales generales */}
                <View style={S.rowTotal}>
                    <Text style={[S.cellT, { flex: 2.5 }]}>TOTAL</Text>
                    {totales.map((val, i) => (
                        <Text key={i} style={[S.cellT, { flex: 1.2 }, S.right]}>
                            {formatNum(val)}
                        </Text>
                    ))}
                </View>
            </Page>
        </Document>
    )
}

export const exportEstadoToPdf = (data, aditionalCols = []) => {
    const docName   = getDocName()
    const sheetName = config.simpleName ?? ''
    pdf(<EstadoDocument data={data} aditionalCols={aditionalCols} sheetName={sheetName} />)
        .toBlob()
        .then(blob => {
            const link = document.createElement('a')
            link.href  = URL.createObjectURL(blob)
            link.download = docName + '_estado_cuentas.pdf'
            link.click()
        })
}

export const exportEstadoToExcel = (data, aditionalCols = []) => {
    const docName = getDocName()
    const rows = []

    data.forEach(item => {
        const ing  = item.desglose?.ingresos
        const egr  = item.desglose?.egresos
        const neto = item.desglose?.neto

        // Cabeceras dinámicas de cantidades
        const cantidadesObj = {}
        aditionalCols.forEach((col, i) => {
            cantidadesObj[col] = item.cantidades?.[i] != null
                ? formatNum(item.cantidades[i])
                : ''
        })

        // Fila principal
        rows.push({ 'Cuenta': item.nombre, ...cantidadesObj, 'Concepto': '', 'Monto Bs.': '' })

        // Ingresos
        if (ing) {
            rows.push({ 'Cuenta': '', ...Object.fromEntries(aditionalCols.map(c => [c, ''])), 'Concepto': '▸ Ingresos', 'Monto Bs.': '' })
            ING_ROWS.forEach(r => {
                rows.push({ 'Cuenta': '', ...Object.fromEntries(aditionalCols.map(c => [c, ''])), 'Concepto': `  ${r.label}`, 'Monto Bs.': formatNum(ing[r.key]) })
            })
        }

        // Egresos
        if (egr) {
            rows.push({ 'Cuenta': '', ...Object.fromEntries(aditionalCols.map(c => [c, ''])), 'Concepto': '▸ Egresos', 'Monto Bs.': '' })
            if (egr.conceptos?.length > 0) {
                egr.conceptos.forEach(c => {
                    rows.push({ 'Cuenta': '', ...Object.fromEntries(aditionalCols.map(col => [col, ''])), 'Concepto': `  ${c.tipo}`, 'Monto Bs.': formatNum(c.totalAbs) })
                })
            }
            rows.push({ 'Cuenta': '', ...Object.fromEntries(aditionalCols.map(c => [c, ''])), 'Concepto': '  Total egresos', 'Monto Bs.': formatNum(egr.total_egresos_abs) })
        }

        // Neto
        if (neto != null) {
            rows.push({ 'Cuenta': '', ...Object.fromEntries(aditionalCols.map(c => [c, ''])), 'Concepto': '  Neto', 'Monto Bs.': formatNum(neto) })
        }

        // Separador
        rows.push({})
    })

    // Totales generales
    const totalesObj = {}
    aditionalCols.forEach((col, i) => {
        const total = data.reduce((acc, row) => acc + (row.cantidades?.[i] ?? 0), 0)
        totalesObj[col] = formatNum(total)
    })
    rows.push({ 'Cuenta': 'TOTAL', ...totalesObj, 'Concepto': '', 'Monto Bs.': '' })

    exportJsonToExcel([{ sheetName: 'Estado de Cuentas', details: rows }], docName, true)
}